import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  Chip,
  Box,
  Grid,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  Alert,
  Pagination,
  Link,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Block as BlockIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { 
  getUrlCorrections, 
  updateUrlCorrection, 
  deleteUrlCorrection, 
  bulkResolveUrlCorrections,
  getUrlCorrectionStats,
  ignoreUrlCorrection,
  unignoreUrlCorrection,
  UrlCorrection,
  UrlCorrectionStatsResponse
} from '../../services/api';

interface UrlCorrectionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UrlCorrectionModal: React.FC<UrlCorrectionModalProps> = ({ isOpen, onClose }) => {
  const [corrections, setCorrections] = useState<UrlCorrection[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [status, setStatus] = useState<'all' | 'resolved' | 'unresolved' | 'ignored'>('unresolved');
  const [stats, setStats] = useState<UrlCorrectionStatsResponse | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editUrl, setEditUrl] = useState('');
  const [selectedCorrections, setSelectedCorrections] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) {
      fetchCorrections();
      fetchStats();
    }
  }, [isOpen, currentPage, status]);

  const fetchCorrections = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getUrlCorrections(currentPage, 20, status);
      setCorrections(response.corrections);
      setTotalPages(response.pagination.pages);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch URL corrections');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getUrlCorrectionStats();
      setStats(response);
    } catch (err: any) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleUpdateCorrection = async (correctionId: string, correctedUrl: string) => {
    try {
      await updateUrlCorrection(correctionId, correctedUrl);
      setEditingId(null);
      setEditUrl('');
      fetchCorrections();
      fetchStats();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update URL correction');
    }
  };

  const handleDeleteCorrection = async (correctionId: string) => {
    if (!window.confirm('Are you sure you want to delete this URL correction?')) {
      return;
    }
    
    try {
      await deleteUrlCorrection(correctionId);
      fetchCorrections();
      fetchStats();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete URL correction');
    }
  };

  const handleBulkResolve = async () => {
    if (selectedCorrections.size === 0) {
      setError('No corrections selected');
      return;
    }

    const bulkCorrections = Array.from(selectedCorrections).map(id => {
      const correction = corrections.find(c => c.id === id);
      return {
        id,
        corrected_url: correction?.corrected_url || ''
      };
    }).filter(c => c.corrected_url);

    if (bulkCorrections.length === 0) {
      setError('No valid corrected URLs found for selected items');
      return;
    }

    try {
      await bulkResolveUrlCorrections(bulkCorrections);
      setSelectedCorrections(new Set());
      fetchCorrections();
      fetchStats();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to bulk resolve corrections');
    }
  };

  const toggleSelection = (correctionId: string) => {
    const newSelection = new Set(selectedCorrections);
    if (newSelection.has(correctionId)) {
      newSelection.delete(correctionId);
    } else {
      newSelection.add(correctionId);
    }
    setSelectedCorrections(newSelection);
  };

  const startEdit = (correction: UrlCorrection) => {
    setEditingId(correction.id);
    setEditUrl(correction.corrected_url || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditUrl('');
  };

  const handleIgnoreCorrection = async (correctionId: string) => {
    if (!window.confirm('Are you sure you want to ignore this stock? It will be excluded from future scraping.')) {
      return;
    }
    
    try {
      await ignoreUrlCorrection(correctionId);
      fetchCorrections();
      fetchStats();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to ignore stock');
    }
  };

  const handleUnignoreCorrection = async (correctionId: string) => {
    try {
      await unignoreUrlCorrection(correctionId);
      fetchCorrections();
      fetchStats();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to unignore stock');
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">URL Corrections</Typography>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Explanation Section */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>What are URL Corrections?</strong> Non-.NS stocks (like .BO stocks) are automatically excluded from scraping because they may not work with screener.in URLs.
          </Typography>
          <Typography variant="body2">
            <strong>Actions:</strong> 
            • <strong>Edit (✏️):</strong> Provide correct screener.in URL to include in scraping
            • <strong>Ignore (🚫):</strong> Skip permanently but keep entry for reference
            • <strong>Delete (🗑️):</strong> Remove completely (may reappear if stock list is reloaded)
          </Typography>
        </Alert>

        {/* Stats */}
        {stats && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={3}>
              <Card>
                <CardContent>
                  <Typography color="error" variant="body2">Unresolved</Typography>
                  <Typography variant="h4" color="error">{stats.unresolved_corrections}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={3}>
              <Card>
                <CardContent>
                  <Typography color="success.main" variant="body2">Resolved</Typography>
                  <Typography variant="h4" color="success.main">{stats.resolved_corrections}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={3}>
              <Card>
                <CardContent>
                  <Typography color="warning.main" variant="body2">Ignored</Typography>
                  <Typography variant="h4" color="warning.main">{stats.ignored_corrections}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={3}>
              <Card>
                <CardContent>
                  <Typography color="primary" variant="body2">Total</Typography>
                  <Typography variant="h4" color="primary">{stats.total_corrections}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Controls */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box display="flex" gap={2}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                label="Status"
                onChange={(e) => {
                  setStatus(e.target.value as any);
                  setCurrentPage(1);
                }}
                              >
                  <MenuItem value="unresolved">Unresolved</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="ignored">Ignored</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
            </FormControl>
            
            {selectedCorrections.size > 0 && (
              <Button
                variant="contained"
                color="success"
                onClick={handleBulkResolve}
              >
                Bulk Resolve ({selectedCorrections.size})
              </Button>
            )}
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            Page {currentPage} of {totalPages}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Table */}
        <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : corrections.length === 0 ? (
            <Box display="flex" justifyContent="center" p={4}>
              <Typography color="text.secondary">No URL corrections found</Typography>
            </Box>
          ) : (
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCorrections(new Set(corrections.map(c => c.id)));
                        } else {
                          setSelectedCorrections(new Set());
                        }
                      }}
                      checked={selectedCorrections.size === corrections.length && corrections.length > 0}
                    />
                  </TableCell>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Original URL</TableCell>
                  <TableCell>Corrected URL</TableCell>
                  <TableCell>Error</TableCell>
                  <TableCell align="center">Count</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {corrections.map((correction) => (
                  <TableRow key={correction.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedCorrections.has(correction.id)}
                        onChange={() => toggleSelection(correction.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {correction.stock_symbol}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Link
                        href={correction.original_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                      >
                        {correction.original_url.replace('https://www.screener.in/company/', '...')}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {editingId === correction.id ? (
                        <Box display="flex" gap={1} alignItems="center">
                          <TextField
                            size="small"
                            value={editUrl}
                            onChange={(e) => setEditUrl(e.target.value)}
                            placeholder="https://www.screener.in/company/..."
                            fullWidth
                          />
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleUpdateCorrection(correction.id, editUrl)}
                          >
                            <SaveIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={cancelEdit}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box>
                          {correction.corrected_url ? (
                            <Link
                              href={correction.corrected_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="body2"
                              color="success.main"
                            >
                              {correction.corrected_url.replace('https://www.screener.in/company/', '...')}
                            </Link>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Not set
                            </Typography>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap title={correction.error_message}>
                        {correction.error_message}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={correction.error_count}
                        size="small"
                        color={
                          correction.error_count > 5 ? 'error' :
                          correction.error_count > 2 ? 'warning' : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={
                          correction.is_ignored ? 'Ignored' :
                          correction.is_resolved ? 'Resolved' : 'Unresolved'
                        }
                        size="small"
                        color={
                          correction.is_ignored ? 'warning' :
                          correction.is_resolved ? 'success' : 'error'
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={1}>
                        {!correction.is_resolved && !correction.is_ignored && (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => startEdit(correction)}
                            title="Edit URL"
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                        {correction.is_ignored ? (
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleUnignoreCorrection(correction.id)}
                            title="Unignore: Allow this stock to be scraped again if URL is corrected"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        ) : (
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleIgnoreCorrection(correction.id)}
                            title="Ignore: Skip this stock permanently in future scraping (keeps entry for reference)"
                          >
                            <BlockIcon />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteCorrection(correction.id)}
                          title="Delete: Completely remove this entry from the database (stock may reappear in future)"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              color="primary"
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UrlCorrectionModal; 